import { useState } from 'react';
import type { CrmMyData } from '@aviaconsole/shared';
import { useI18n, type Key } from '../i18n';
import { formatDate } from '../lib/format';
import { Button, Card, ConfirmDialog, Field } from './ui';

/**
 * The records a person sees on "My data" (AVI-13 §8a), shared by the portal
 * and the public emailed-link page: everything held about them, with the two
 * things they can do about it.
 */
export function MyDataView({ data, email, pending, onOptOut, onRequestErasure }: {
  data: CrmMyData; email: string; pending: boolean; onOptOut: () => void; onRequestErasure: () => void;
}) {
  const { t, lang } = useI18n();
  const [confirmErase, setConfirmErase] = useState(false);
  if (!data.records.length) return <Card><p className="secondary" style={{ margin: 0 }}>{t('myDataNone', { email })}</p></Card>;
  const optedOut = data.records.every((r) => r.optedOutAt);
  const erasureAsked = data.records.every((r) => r.erasureRequestedAt);
  return (
    <div className="stack">
      {data.records.map((r) => (
        <Card key={r.personId}>
          <h2 style={{ marginTop: 0 }}>{r.name}{r.roleTitle ? ` · ${r.roleTitle}` : ''}</h2>
          <div className="grid two">
            <Field label={t('myDataAccount')} value={r.accountName} />
            <Field label={t('fEmail')} value={r.email} />
            <Field label={t('fPhone')} value={r.phone} />
            <Field label="LinkedIn" value={r.linkedinUrl} />
            <Field label={t('myDataBasis')} value={t(`basis_${r.lawfulBasis}` as Key)} hint={r.basisNote ?? undefined} />
            <Field label={t('myDataSource')} value={r.source} />
          </div>
          {r.optedOutAt && <p className="small" style={{ color: 'var(--success)' }}>{t('myDataOptedOut', { date: formatDate(r.optedOutAt, lang) })}</p>}
          {r.erasureRequestedAt && <p className="small" style={{ color: 'var(--warning)' }}>{t('myDataErasureRequested', { date: formatDate(r.erasureRequestedAt, lang) })}</p>}
          <h3>{t('myDataTimeline')}</h3>
          <div className="stack" style={{ gap: '0.5rem' }}>
            {r.activities.length === 0 && <div className="small muted">{t('nothingHere')}</div>}
            {r.activities.map((a, i) => (
              <div key={i} className="subcard">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.84rem' }}>{a.subject}</span>
                  <span className="mono small muted">{formatDate(a.occurredAt, lang, true)}</span>
                </div>
                {a.body && <p className="small secondary" style={{ whiteSpace: 'pre-wrap', margin: '0.3rem 0 0' }}>{a.body}</p>}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <Button icon="fas fa-ban" disabled={pending || optedOut} onClick={onOptOut}>{t('myDataOptOut')}</Button>
        <Button variant="danger" icon="fas fa-eraser" disabled={pending || erasureAsked} onClick={() => setConfirmErase(true)}>{t('myDataRequestErasure')}</Button>
      </div>
      {confirmErase && (
        <ConfirmDialog title={t('myDataConfirmErasureTitle')} confirmLabel={t('myDataRequestErasure')} cancelLabel={t('cancel')} danger pending={pending}
          onClose={() => setConfirmErase(false)} onConfirm={() => { onRequestErasure(); setConfirmErase(false); }}>
          {t('myDataConfirmErasureBody')}
        </ConfirmDialog>
      )}
    </div>
  );
}
